// Video URL validation and embed utilities
import env from '../env';

/**
 * Domain TikTok uses to verify iframe embed approval.
 * Uses current hostname in the browser, or FRONTEND_URL hostname as fallback.
 */
export const getTikTokEmbedDomain = () => {
    if (typeof window !== 'undefined' && window.location?.hostname) {
        return window.location.hostname;
    }

    try {
        return new URL(env.FRONTEND_URL).hostname;
    } catch {
        return 'maveshibids.com';
    }
};

/**
 * Builds the official TikTok LIVE iframe embed URL.
 * @see https://www.tiktok.com/embed/live/@username
 */
export const buildTikTokLiveEmbedUrl = (username, embedDomain = getTikTokEmbedDomain()) => {
    const params = new URLSearchParams({
        autoplay: '1',
        muted: '1',
        controls: '1',
        embed_domain: embedDomain
    });

    return `https://www.tiktok.com/embed/live/@${username}?${params.toString()}`;
};

/**
 * Validates if a URL is a supported video platform
 * @param {string} url - The video URL to validate
 * @returns {object} - { isValid: boolean, platform: string, videoId: string, embedUrl: string }
 */
export const validateVideoUrl = (url) => {
    if (!url || typeof url !== 'string') {
        return { isValid: false, platform: null, videoId: null, embedUrl: null };
    }

    // YouTube patterns (including Shorts)
    const youtubePatterns = [
        /(?:youtube\.com\/watch\?v=|youtu\.be\/|youtube\.com\/embed\/)([^&\n?#]+)/,
        /youtube\.com\/watch\?.*v=([^&\n?#]+)/,
        /youtube\.com\/shorts\/([^&\n?#]+)/
    ];

    // Vimeo patterns
    const vimeoPatterns = [
        /vimeo\.com\/(\d+)/,
        /player\.vimeo\.com\/video\/(\d+)/
    ];

    // TikTok patterns
    const tiktokPatterns = [
        /tiktok\.com\/@[\w.-]+\/video\/(\d+)/,
        /tiktok\.com\/v\/(\d+)/
    ];

    // Check YouTube
    for (const pattern of youtubePatterns) {
        const match = url.match(pattern);
        if (match) {
            const videoId = match[1];
            return {
                isValid: true,
                platform: 'youtube',
                videoId: videoId,
                embedUrl: `https://www.youtube.com/embed/${videoId}`
            };
        }
    }

    // Check Vimeo
    for (const pattern of vimeoPatterns) {
        const match = url.match(pattern);
        if (match) {
            const videoId = match[1];
            return {
                isValid: true,
                platform: 'vimeo',
                videoId: videoId,
                embedUrl: `https://player.vimeo.com/video/${videoId}`
            };
        }
    }

    // Check TikTok
    for (const pattern of tiktokPatterns) {
        const match = url.match(pattern);
        if (match) {
            const videoId = match[1];
            return {
                isValid: true,
                platform: 'tiktok',
                videoId: videoId,
                embedUrl: `https://www.tiktok.com/player/v1/${videoId}?music_info=1&description=1`
            };
        }
    }

    return { isValid: false, platform: null, videoId: null, embedUrl: null };
};

/**
 * Validates live stream URLs (YouTube, TikTok, Facebook)
 * @param {string} url - The live stream URL to validate
 * @returns {object} - { isValid, platform, videoId, embedUrl, originalUrl }
 */
export const validateLiveStreamUrl = (url) => {
    if (!url || typeof url !== 'string') {
        return { isValid: false, platform: null, videoId: null, embedUrl: null, originalUrl: null };
    }

    const trimmedUrl = url.trim();

    const youtubePatterns = [
        /(?:youtube\.com\/watch\?v=|youtu\.be\/|youtube\.com\/embed\/|youtube\.com\/live\/)([^&\n?#\/]+)/,
        /youtube\.com\/watch\?.*v=([^&\n?#]+)/
    ];

    for (const pattern of youtubePatterns) {
        const match = trimmedUrl.match(pattern);
        if (match) {
            const videoId = match[1];
            return {
                isValid: true,
                platform: 'youtube',
                videoId,
                embedType: 'iframe',
                embedUrl: `https://www.youtube.com/embed/${videoId}?autoplay=1&rel=0`,
                originalUrl: trimmedUrl
            };
        }
    }

    const tiktokLiveEmbedMatch = trimmedUrl.match(/tiktok\.com\/embed\/live\/@([\w.-]+)/i);
    const tiktokLiveMatch = trimmedUrl.match(/tiktok\.com\/@([\w.-]+)\/live/i);

    if (tiktokLiveEmbedMatch || tiktokLiveMatch) {
        const username = (tiktokLiveEmbedMatch || tiktokLiveMatch)[1];
        return {
            isValid: true,
            platform: 'tiktok',
            videoId: username,
            embedType: 'tiktok-live',
            embedUrl: null,
            originalUrl: trimmedUrl
        };
    }

    const tiktokVideoPatterns = [
        /tiktok\.com\/@[\w.-]+\/video\/(\d+)/,
        /tiktok\.com\/v\/(\d+)/
    ];

    for (const pattern of tiktokVideoPatterns) {
        const match = trimmedUrl.match(pattern);
        if (match) {
            const videoId = match[1];
            return {
                isValid: true,
                platform: 'tiktok',
                videoId,
                embedType: 'iframe',
                embedUrl: `https://www.tiktok.com/player/v1/${videoId}?music_info=0&description=0&controls=1`,
                originalUrl: trimmedUrl
            };
        }
    }

    const facebookPatterns = [
        /facebook\.com\/watch\/live\/?\?v=(\d+)/i,
        /facebook\.com\/watch\/?\?v=(\d+)/i,
        /facebook\.com\/.*\/videos\/(\d+)/i,
        /facebook\.com\/live\/(\d+)/i,
        /fb\.watch\/([^\/\?&]+)/i
    ];

    for (const pattern of facebookPatterns) {
        const match = trimmedUrl.match(pattern);
        if (match) {
            return {
                isValid: true,
                platform: 'facebook',
                videoId: match[1],
                embedType: 'iframe',
                embedUrl: `https://www.facebook.com/plugins/video.php?href=${encodeURIComponent(trimmedUrl)}&show_text=false&width=560&height=315&autoplay=true`,
                originalUrl: trimmedUrl
            };
        }
    }

    if (/facebook\.com/i.test(trimmedUrl) || /fb\.watch/i.test(trimmedUrl)) {
        return {
            isValid: true,
            platform: 'facebook',
            videoId: null,
            embedType: 'iframe',
            embedUrl: `https://www.facebook.com/plugins/video.php?href=${encodeURIComponent(trimmedUrl)}&show_text=false&width=560&height=315&autoplay=true`,
            originalUrl: trimmedUrl
        };
    }

    return { isValid: false, platform: null, videoId: null, embedUrl: null, originalUrl: null };
};

/**
 * Live stream URL validation rule for Ant Design forms
 */
export const liveStreamUrlValidator = (_, value) => {
    if (!value) {
        return Promise.resolve();
    }

    const validation = validateLiveStreamUrl(value);
    if (!validation.isValid) {
        return Promise.reject(new Error('Please enter a valid YouTube, TikTok, or Facebook live stream URL'));
    }

    return Promise.resolve();
};

export const SUPPORTED_LIVE_STREAM_PLATFORMS = {
    youtube: {
        name: 'YouTube Live',
        example: 'https://youtube.com/watch?v=VIDEO_ID or https://youtube.com/live/VIDEO_ID',
        icon: '📺'
    },
    tiktok: {
        name: 'TikTok Live',
        example: 'https://www.tiktok.com/@username/live',
        icon: '📱'
    },
    facebook: {
        name: 'Facebook Live',
        example: 'https://facebook.com/watch/live/?v=VIDEO_ID',
        icon: '👤'
    }
};

/**
 * Generates a thumbnail URL for the video
 * @param {string} platform - The video platform (youtube, vimeo, tiktok)
 * @param {string} videoId - The video ID
 * @returns {string} - Thumbnail URL
 */
export const getVideoThumbnail = (platform, videoId) => {
    if (platform === 'youtube') {
        return `https://img.youtube.com/vi/${videoId}/maxresdefault.jpg`;
    } else if (platform === 'vimeo') {
        // Vimeo requires API call for thumbnail, so we'll use a placeholder
        return `https://vumbnail.com/${videoId}.jpg`;
    } else if (platform === 'tiktok') {
        // TikTok doesn't provide a direct thumbnail URL by ID easily without API
        // We'll use a placeholder or return null
        return null; 
    }
    return null;
};

/**
 * Video URL validation rule for Ant Design forms
 */
export const videoUrlValidator = (_, value) => {
    if (!value) {
        return Promise.resolve();
    }

    const validation = validateVideoUrl(value);
    if (!validation.isValid) {
        return Promise.reject(new Error('Please enter a valid YouTube, Vimeo, or TikTok URL'));
    }

    return Promise.resolve();
};

/**
 * Supported video platforms
 */
export const SUPPORTED_PLATFORMS = {
    youtube: {
        name: 'YouTube',
        example: 'https://youtube.com/watch?v=VIDEO_ID, https://youtu.be/VIDEO_ID, or https://youtube.com/shorts/VIDEO_ID',
        icon: '🎥'
    },
    vimeo: {
        name: 'Vimeo',
        example: 'https://vimeo.com/VIDEO_ID',
        icon: '🎬'
    },
    tiktok: {
        name: 'TikTok',
        example: 'https://www.tiktok.com/@user/video/VIDEO_ID',
        icon: '📱'
    }
};

/**
 * Gets the platform name and icon for display
 * @param {string} platform - The platform identifier
 * @returns {object} - { name: string, icon: string }
 */
export const getPlatformInfo = (platform) => {
    return SUPPORTED_PLATFORMS[platform] || { name: 'Unknown', icon: '🎥' };
};
